import { useMemo, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  PRODUCTIONS,
  REVIEWS,
  galleryForProduction,
  imageForProduction,
  enrichProduction,
  TICKETS_URL,
} from "../data/reviews";
import SEO from "../components/SEO";
import Stars from "../components/Stars";
import MagneticButton from "../components/MagneticButton";

/**
 * Deep show page — /productions/:slug
 * Hero photo + editorial masthead, synopsis, gallery grid, all press reviews,
 * ticket CTA, and TheaterEvent + Review JSON-LD for SEO.
 */
export default function Production() {
  const { slug } = useParams();
  const raw = PRODUCTIONS.find((p) => p.slug === slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const reviews = useMemo(
    () => (raw ? REVIEWS.filter((r) => r.slug === raw.slug) : []),
    [raw],
  );

  if (!raw) return <Navigate to="/reviews" replace />;

  const prod = enrichProduction(raw);
  const heroImg = imageForProduction(raw.slug);
  const gallery = galleryForProduction(raw.slug);

  const idx = PRODUCTIONS.findIndex((p) => p.slug === raw.slug);
  const prev = idx > 0 ? PRODUCTIONS[idx - 1] : null;
  const next = idx < PRODUCTIONS.length - 1 ? PRODUCTIONS[idx + 1] : null;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "TheaterEvent",
      name: prod.title,
      description: prod.synopsis,
      performer: { "@type": "TheaterGroup", name: "COLAB Theatre" },
      startDate: String(prod.year),
      location: { "@type": "Place", name: prod.venue, address: "London, UK" },
      genre: prod.genre,
      image: heroImg,
      offers: {
        "@type": "Offer",
        url: TICKETS_URL,
        availability: "https://schema.org/InStock",
      },
      ...(prod.average_rating && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: prod.average_rating,
          reviewCount: prod.review_count,
          bestRating: 5,
          worstRating: 1,
        },
      }),
    },
    ...reviews.map((r) => ({
      "@context": "https://schema.org",
      "@type": "Review",
      itemReviewed: {
        "@type": "TheaterEvent",
        name: r.show,
        performer: { "@type": "TheaterGroup", name: "COLAB Theatre" },
      },
      author: { "@type": "Person", name: r.reviewer },
      publisher: { "@type": "Organization", name: r.outlet },
      reviewBody: `${r.quote} — ${r.summary}`,
      url: r.url,
      datePublished: String(r.date),
      ...(r.rating !== null && {
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
      }),
    })),
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "/" },
        { "@type": "ListItem", position: 2, name: "Reviews", item: "/reviews" },
        { "@type": "ListItem", position: 3, name: prod.title, item: `/productions/${slug}` },
      ],
    },
  ];

  return (
    <main data-testid="production-page" className="min-h-screen bg-[#050505]">
      <SEO
        title={`${prod.title} — COLAB Theatre | ${prod.genre} in London (${prod.year})`}
        description={`${prod.synopsis} ${prod.review_count} press reviews from the London immersive theatre press. ${prod.hero_quote ? '"' + prod.hero_quote + '" — ' + prod.hero_outlet + '.' : ''} Best immersive theatre in London.`}
        canonical={`https://www.colabtheatre.co.uk/productions/${slug}`}
        image={heroImg}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] w-full overflow-hidden"
      >
        <motion.div style={{ scale: bgScale, y: bgY }} className="absolute inset-0" aria-hidden="true">
          <img
            src={heroImg}
            alt={`${prod.title} — Best immersive theatre in London. COLAB Theatre immersive theatre production still (${prod.year}). ${prod.genre} at ${prod.venue}.`}
            className="w-full h-full object-cover opacity-50"
            style={{ filter: "grayscale(0.25) contrast(1.1) brightness(0.6)" }}
          />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_20%,#050505_92%)]" />
        </motion.div>

        <div className="relative z-10 h-[100svh] flex flex-col justify-between px-6 md:px-14 lg:px-20 xl:px-24 pt-32 md:pt-40 pb-12">
          <div className="flex items-center justify-between text-[10px] md:text-xs uppercase tracking-[0.35em] font-mono text-white/60">
            <div className="flex items-center gap-3">
              <span className="inline-block w-2 h-2 bg-[#8b0000]" />
              <span>Chapter {prod.chapter} · {prod.year}</span>
            </div>
            <div className="hidden md:block">{prod.genre}</div>
            <div>{prod.venue}</div>
          </div>

          <div>
            <motion.h1
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="hero-title text-white select-none"
              data-testid="production-title"
            >
              {prod.title}
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.9 }}
              className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end"
            >
              <p className="md:col-span-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed">
                {prod.synopsis}
              </p>
              <div className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end">
                <MagneticButton
                  href={TICKETS_URL}
                  variant="solid"
                  testid="production-cta-tickets"
                >
                  Book tickets →
                </MagneticButton>
                <MagneticButton
                  to="/reviews"
                  variant="ghost"
                  testid="production-cta-reviews"
                >
                  All reviews
                </MagneticButton>
              </div>
            </motion.div>
          </div>

          <div className="flex items-end justify-between text-[10px] uppercase tracking-[0.3em] font-mono text-white/50">
            <div className="flex items-center gap-4">
              <Link to="/reviews" className="link-hover">← The Archive</Link>
              <span className="w-1 h-1 bg-white/30 rounded-full" />
              <a
                href={raw.official_url}
                target="_blank"
                rel="noreferrer"
                data-testid="production-hero-official"
                className="link-hover text-white/70 hover:text-white"
              >
                {raw.title} on colabtheatre.co.uk ↗
              </a>
            </div>
            <div className="flex items-center gap-6">
              <span>{prod.review_count} reviews</span>
              {prod.rave_count > 0 && (
                <>
                  <span className="w-1 h-1 bg-white/40 rounded-full" />
                  <span>{prod.rave_count} raves</span>
                </>
              )}
              {prod.average_rating && (
                <>
                  <span className="w-1 h-1 bg-white/40 rounded-full" />
                  <span>{prod.average_rating}/5 avg</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* HERO QUOTE + META */}
      {prod.hero_quote && (
        <section className="section py-24 md:py-32 px-6 md:px-14 lg:px-20 xl:px-24 border-t border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-3 text-[10px] uppercase tracking-[0.35em] font-mono text-[#8b0000]">
              The Verdict
            </div>
            <blockquote className="md:col-span-9">
              <div className="font-display text-4xl md:text-7xl leading-[0.95] text-white uppercase">
                &ldquo;{prod.hero_quote}&rdquo;
              </div>
              <div className="mt-8 text-[10px] uppercase tracking-[0.3em] font-mono text-white/50">
                — {prod.hero_outlet}
              </div>
            </blockquote>
          </div>
        </section>
      )}

      {/* GALLERY */}
      <section
        data-testid="production-gallery"
        className="px-6 md:px-14 lg:px-20 xl:px-24 pb-24 md:pb-32 border-t border-white/10 pt-16"
      >
        <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-white/50 mb-10">
          {prod.title} · Immersive Theatre Production Stills · Best Immersive Theatre London
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          {gallery.map((src, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              className={`filmstrip ${
                i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : ""
              }`}
            >
              <img src={src} alt={`${prod.title} — Best immersive theatre in London. COLAB Theatre immersive theatre production still ${i + 1} (${prod.year}) — ${prod.genre}.`} loading="lazy" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section
        data-testid="production-reviews"
        className="px-6 md:px-14 lg:px-20 xl:px-24 pb-24 md:pb-32 border-t border-white/10 pt-20"
      >
        <div className="flex items-end justify-between mb-14">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#8b0000] mb-4">
              {prod.title} · {reviews.length} Immersive Theatre Reviews
            </div>
            <h2 className="bleed-h max-w-4xl text-white">
              Immersive theatre reviews.
              <span className="block text-white/40">Best immersive theatre London.</span>
            </h2>
          </div>
        </div>

        <ul className="border-t border-white/10">
          {reviews.map((r, i) => (
            <li
              key={i}
              className="border-b border-white/10 hover:bg-white/[0.02] transition-colors"
            >
              <a
                href={r.url}
                target="_blank"
                rel="noreferrer"
                className="block px-1 md:px-4 py-6 md:py-8"
                data-testid={`production-review-${i}`}
              >
                <div className="grid grid-cols-12 gap-4 md:gap-8 items-baseline">
                  <div className="col-span-4 md:col-span-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/60">
                      {r.outlet}
                    </div>
                    <div className="mt-1 text-white/40 text-xs">
                      {r.reviewer}
                    </div>
                  </div>
                  <div className="col-span-8 md:col-span-7">
                    <div className="font-display text-xl md:text-3xl leading-tight text-white group-hover:text-[#c9a961]">
                      &ldquo;{r.quote}&rdquo;
                    </div>
                    <div className="mt-3 text-white/50 text-sm max-w-3xl">
                      {r.summary}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex md:justify-end items-center gap-4">
                    {r.rating !== null ? (
                      <Stars value={r.rating} />
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/40">
                        Editorial
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/30">
                      ↗
                    </span>
                  </div>
                </div>
              </a>
            </li>
          ))}
        </ul>
      </section>

      {/* PREV / NEXT + BOOK */}
      <section className="px-6 md:px-14 lg:px-20 xl:px-24 py-24 md:py-32 border-t border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#8b0000] mb-6">
              Now Booking
            </div>
            <h2 className="bleed-h max-w-3xl text-white">
              See what&apos;s on.
            </h2>
            <p className="mt-8 text-white/60 max-w-lg leading-relaxed">
              COLAB Theatre&apos;s current billing is always live on
              colabtheatre.co.uk/whatson. Come and see why we&apos;re called the
              best immersive theatre in London.
            </p>
          </div>
          <div className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end">
            <MagneticButton
              href={TICKETS_URL}
              variant="velvet"
              testid="production-book-cta"
            >
              Book tickets →
            </MagneticButton>
            <MagneticButton
              href={raw.official_url}
              variant="solid"
              testid="production-official-cta"
            >
              View on colabtheatre.co.uk ↗
            </MagneticButton>
            <MagneticButton href="/api/press-kit.pdf" variant="ghost" testid="production-press-kit">
              Press kit (PDF)
            </MagneticButton>
          </div>
        </div>

        <div className="mt-20 md:mt-32 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-white/10 pt-10">
          {prev ? (
            <Link
              to={`/productions/${prev.slug}`}
              className="group block"
              data-testid="production-prev"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/40 mb-2">
                ← Previous · Chapter {prev.chapter}
              </div>
              <div className="font-display text-3xl md:text-5xl uppercase text-white/70 group-hover:text-white transition-colors">
                {prev.title}
              </div>
            </Link>
          ) : <div />}
          {next ? (
            <Link
              to={`/productions/${next.slug}`}
              className="group block md:text-right"
              data-testid="production-next"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/40 mb-2">
                Next · Chapter {next.chapter} →
              </div>
              <div className="font-display text-3xl md:text-5xl uppercase text-white/70 group-hover:text-white transition-colors">
                {next.title}
              </div>
            </Link>
          ) : <div />}
        </div>
      </section>
    </main>
  );
}
